import React, { useMemo, useState } from 'react';
import { Plus, Search, Users } from 'lucide-react';
import { CONTACT_CATEGORIES } from './helpers';
import ContactCard from './ContactCard';

export default function ContactsTab({ contacts, onNewContact, onEditContact, onDeleteContact }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      if (filter !== 'all' && c.category !== filter) return false;
      if (!q) return true;
      const hay = [c.name, c.mobile, c.email, c.bank, c.branch, c.designation,
        c.agency, c.relation, c.property_location, c.notes].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [contacts, search, filter]);

  // counts per category
  const counts = useMemo(() => {
    const out = { all: contacts.length };
    for (const c of CONTACT_CATEGORIES) out[c.id] = contacts.filter((x) => x.category === c.id).length;
    return out;
  }, [contacts]);

  return (
    <div data-testid="contacts-tab">
      <div className="contacts-toolbar">
        <div className="contacts-search">
          <Search className="contacts-search-icon" />
          <input className="contacts-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, mobile, bank, agency…" data-testid="contacts-search" />
        </div>
        <button className="btn-primary" onClick={onNewContact} data-testid="new-contact-btn">
          <Plus className="icon" /> New Contact
        </button>
      </div>

      <div className="contacts-filter-chips" data-testid="contacts-filter-chips">
        <button className={`contacts-filter-chip${filter === 'all' ? ' selected' : ''}`}
          onClick={() => setFilter('all')} data-testid="filter-all">
          All <span className="count">{counts.all}</span>
        </button>
        {CONTACT_CATEGORIES.map((c) => (
          <button key={c.id}
            className={`contacts-filter-chip cat-${c.id}${filter === c.id ? ' selected' : ''}`}
            onClick={() => setFilter(c.id)} data-testid={`filter-${c.id}`}>
            {c.label} <span className="count">{counts[c.id] || 0}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="empty" data-testid="contacts-empty">
          <div className="empty-icon"><Users style={{ width: 40, height: 40, stroke: '#a8a29e' }} /></div>
          <h3>{contacts.length === 0 ? 'No contacts yet' : 'No matching contacts'}</h3>
          <p>{contacts.length === 0
            ? 'Build your address book here — bank staff, clients, DSAs, and referrals you work with.'
            : 'Try clearing the search or changing the filter.'}</p>
          {contacts.length === 0 && (
            <div className="empty-actions">
              <button className="btn-primary" onClick={onNewContact}><Plus className="icon" /> Add First Contact</button>
            </div>
          )}
        </div>
      ) : (
        <div className="contacts-grid" data-testid="contacts-grid">
          {filtered.map((c) => (
            <ContactCard key={c.id} contact={c} onEdit={onEditContact} onDelete={onDeleteContact} />
          ))}
        </div>
      )}
    </div>
  );
}
